import { useEffect, useState, type ReactNode } from 'react';
import { AlertCircle, Check, CreditCard, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  useModelCatalog,
  type VisualModelCatalogGroup,
  type VisualModelCatalogItem,
} from '@/hooks/useModelCatalog';
import type { ContentFormData } from '..';

interface ModelSelectProps {
  formData: ContentFormData;
  updateFormData: (data: Partial<ContentFormData>) => void;
  onValidationChange: (isValid: boolean) => void;
}

function formatCost(group: VisualModelCatalogGroup) {
  if (group.min_credits_per_scene === group.max_credits_per_scene) {
    return `${group.min_credits_per_scene}cr/scene`;
  }
  return `${group.min_credits_per_scene}-${group.max_credits_per_scene}cr/scene`;
}

const MOTION_CODE_LINES: { indent: number; chars: number; tokens: ReactNode }[] = [
  {
    indent: 0,
    chars: 28,
    tokens: (
      <>
        <span className="text-[#ff7b72]">const</span>{' '}
        <span className="text-[#79c0ff]">scene</span>{' '}
        <span className="text-white/70">=</span>{' '}
        <span className="text-[#d2a8ff]">createScene</span>
        <span className="text-white/70">({'{'}</span>
      </>
    ),
  },
  {
    indent: 1,
    chars: 26,
    tokens: (
      <>
        <span className="text-[#79c0ff]">template</span>
        <span className="text-white/70">:</span>{' '}
        <span className="text-[#a5d6ff]">"kinetic-text"</span>
        <span className="text-white/70">,</span>
      </>
    ),
  },
  {
    indent: 1,
    chars: 20,
    tokens: (
      <>
        <span className="text-[#79c0ff]">accent</span>
        <span className="text-white/70">:</span>{' '}
        <span className="text-[#a5d6ff]">"#6366f1"</span>
        <span className="text-white/70">,</span>
      </>
    ),
  },
  {
    indent: 0,
    chars: 3,
    tokens: <span className="text-white/70">{'})'}</span>,
  },
  {
    indent: 0,
    chars: 14,
    tokens: (
      <>
        <span className="text-[#d2a8ff]">render</span>
        <span className="text-white/70">(</span>
        <span className="text-[#79c0ff]">scene</span>
        <span className="text-white/70">)</span>
      </>
    ),
  },
];

function MotionPreview() {
  return (
    <div
      className="relative flex h-full w-full items-center justify-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #14172b 0%, #0a0c14 55%, #181233 100%)' }}
    >
      <style>{`
        @keyframes sr-code-type { from { width: 0 } to { width: 100% } }
        @keyframes sr-code-caret { 0%, 45% { opacity: 1 } 50%, 100% { opacity: 0 } }
        @keyframes sr-code-glow { 0%, 100% { opacity: 0.25 } 50% { opacity: 0.55 } }
      `}</style>
      <div
        className="pointer-events-none absolute -bottom-10 left-1/2 h-32 w-3/4 -translate-x-1/2 rounded-full bg-indigo-500/40 blur-3xl"
        style={{ animation: 'sr-code-glow 4s ease-in-out infinite' }}
      />
      <div className="relative w-[78%] max-w-[340px] overflow-hidden rounded-lg border border-white/10 bg-[#0d1117] shadow-2xl">
        <div className="flex items-center gap-1.5 bg-[#161b22] px-3 py-2">
          <span className="h-2 w-2 rounded-full bg-[#ff5f57]" />
          <span className="h-2 w-2 rounded-full bg-[#febc2e]" />
          <span className="h-2 w-2 rounded-full bg-[#28c840]" />
          <span className="ml-2 text-[9px] font-medium text-white/40">scene.tsx</span>
        </div>
        <div className="px-3 py-2.5 font-mono text-[10px] leading-[1.7]">
          {MOTION_CODE_LINES.map((line, index) => (
            <div key={index} className="flex" style={{ paddingLeft: line.indent * 12 }}>
              <span
                className="inline-block overflow-hidden whitespace-nowrap"
                style={{
                  animation: `sr-code-type 0.6s steps(${line.chars}) ${0.25 + index * 0.55}s both`,
                }}
              >
                {line.tokens}
              </span>
            </div>
          ))}
          <span
            className="mt-0.5 inline-block h-3 w-[5px] bg-indigo-400"
            style={{ animation: 'sr-code-caret 1s step-end infinite' }}
          />
        </div>
      </div>
    </div>
  );
}

function GroupPreview({
  group,
  selectedModel,
  styleOverrideUrl,
}: {
  group: VisualModelCatalogGroup;
  selectedModel?: VisualModelCatalogItem;
  styleOverrideUrl?: string | null;
}) {
  // A chosen style's example image takes priority over everything else
  if (styleOverrideUrl) {
    return (
      <img
        key={styleOverrideUrl}
        src={styleOverrideUrl}
        className="h-full w-full object-cover"
        alt={group.name}
      />
    );
  }
  // The selected model drives the card preview; fall back to the group default.
  const preview =
    selectedModel && (selectedModel.preview_url || selectedModel.preview_type === 'motion')
      ? selectedModel
      : group.preview_url
        ? group
        : group.models[0];
  if (!preview || preview.preview_type === 'motion') return <MotionPreview />;
  if (preview.preview_type === 'video') {
    return (
      <video
        key={preview.preview_url}
        src={preview.preview_url || undefined}
        className="h-full w-full object-cover"
        muted
        autoPlay
        loop
        playsInline
      />
    );
  }
  return (
    <img
      key={preview.preview_url}
      src={preview.preview_url || undefined}
      className="h-full w-full object-cover"
      alt={group.name}
    />
  );
}

export function ModelSelect({ formData, updateFormData, onValidationChange }: ModelSelectProps) {
  const { data, isLoading, isError, refetch } = useModelCatalog();
  const groups = data?.groups || [];
  const currentModel = formData.visualModel || 'auto';
  const activeGroup = groups.find(
    group => group.id === currentModel || group.models.some(model => model.id === currentModel),
  );

  useEffect(() => {
    if (!data) return;
    const isSupported = data.groups.some(
      group => group.id === currentModel || group.models.some(model => model.id === currentModel),
    );
    if (!isSupported) updateFormData({ visualModel: 'auto' });
    onValidationChange(true);
  }, [data, currentModel, onValidationChange, updateFormData]);

  // Remember the last tier picked in each group so switching cards and
  // coming back restores the choice instead of resetting to the default.
  const [lastTier, setLastTier] = useState<Record<string, string>>({});

  // Drop a stale style when the new model can't use it (keeps the payload clean)
  const styleForModel = (modelId: string) => {
    const model = groups.flatMap(g => g.models).find(m => m.id === modelId);
    return model?.styles?.length ? formData.recraftStyle : undefined;
  };

  const selectGroup = (group: VisualModelCatalogGroup) => {
    const modelId = group.id === 'auto' ? 'auto' : lastTier[group.id] || group.default_model_id;
    updateFormData({ visualModel: modelId, recraftStyle: styleForModel(modelId) });
    onValidationChange(true);
  };

  const selectModel = (group: VisualModelCatalogGroup, modelId: string) => {
    setLastTier(prev => ({ ...prev, [group.id]: modelId }));
    updateFormData({ visualModel: modelId, recraftStyle: styleForModel(modelId) });
    onValidationChange(true);
  };

  const currentStyle = formData.recraftStyle;
  const selectStyle = (styleId: string) => {
    updateFormData({ recraftStyle: styleId });
    onValidationChange(true);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-48 items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading visual models...
      </div>
    );
  }

  if (isError || groups.length === 0) {
    return (
      <div className="flex min-h-48 flex-col items-center justify-center gap-3 text-center">
        <AlertCircle className="h-6 w-6 text-destructive" />
        <p className="text-sm text-muted-foreground">Visual models could not be loaded.</p>
        <Button type="button" variant="outline" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const autoGroup = groups.find(group => group.id === 'auto');
  const visualGroups = groups.filter(group => group.id !== 'auto');

  return (
    <div className="space-y-3">
      {autoGroup && (
        <button
          type="button"
          onClick={() => selectGroup(autoGroup)}
          className={cn(
            'flex w-full items-center gap-4 rounded-lg border-2 p-4 text-left transition-colors',
            activeGroup?.id === autoGroup.id
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/40',
          )}
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">{autoGroup.name}</span>
              {autoGroup.recommended && (
                <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                  Recommended
                </span>
              )}
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                {formatCost(autoGroup)}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">{autoGroup.description}</p>
          </div>
          {activeGroup?.id === autoGroup.id && (
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary">
              <Check className="h-3 w-3 text-white" strokeWidth={3} />
            </div>
          )}
        </button>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {visualGroups.map((group, index) => {
          const isActive = activeGroup?.id === group.id;
          const groupChoice = isActive ? currentModel : lastTier[group.id];
          const selectedModel = group.models.find(model => model.id === groupChoice);
          // Style options for the active card's selected model (e.g. Recraft)
          const styleOptions = isActive ? selectedModel?.styles ?? [] : [];
          const effectiveStyle =
            currentStyle && styleOptions.some(s => s.id === currentStyle)
              ? currentStyle
              : styleOptions[0]?.id;
          const styleOverrideUrl = isActive
            ? styleOptions.find(s => s.id === effectiveStyle)?.preview_url
            : undefined;
          // Rows with tiers to pick always show chips on both cards so the
          // pair's heights stay balanced and the options are discoverable.
          // Rows of single-model cards (Stock/Motion) stay chip-free.
          const rowMate = visualGroups[index % 2 === 0 ? index + 1 : index - 1];
          const rowHasTiers =
            group.models.length > 1 || (rowMate?.models.length ?? 0) > 1;
          const showChips = rowHasTiers && group.models.length > 0;
          return (
            <div
              key={group.id}
              role="button"
              tabIndex={0}
              onClick={() => selectGroup(group)}
              onKeyDown={event => {
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter' || event.key === ' ') selectGroup(group);
              }}
              className={cn(
                'min-w-0 overflow-hidden rounded-lg border-2 text-left transition-colors',
                isActive ? 'border-primary' : 'border-border hover:border-primary/40',
              )}
            >
              <div className="relative aspect-video w-full overflow-hidden bg-black">
                <GroupPreview
                  group={group}
                  selectedModel={selectedModel}
                  styleOverrideUrl={styleOverrideUrl}
                />
                {isActive && (
                  <div className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary">
                    <Check className="h-3 w-3 text-white" strokeWidth={3} />
                  </div>
                )}
              </div>

              <div className="min-w-0 space-y-1.5 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold">{group.name}</span>
                  <span className={cn('rounded-full px-1.5 py-0.5 text-xs font-medium', group.badge_color)}>
                    {group.media_label} · {formatCost(group)}
                  </span>
                </div>
                <p className="text-xs font-medium text-foreground/70">{group.tagline}</p>
                <p className="text-xs leading-relaxed text-muted-foreground">{group.description}</p>

                {group.models.length === 1 && !showChips && (
                  <div className="flex items-center gap-1 pt-0.5 text-xs text-muted-foreground">
                    <CreditCard className="h-3 w-3" />
                    <span>{group.models[0].credits_per_scene}cr/scene</span>
                  </div>
                )}

                {/* Model carousel inside the active card — picking one swaps the preview above */}
                {showChips && (
                  <div
                    className="flex snap-x gap-2 overflow-x-auto pt-1.5"
                    onClick={event => event.stopPropagation()}
                    onKeyDown={event => event.stopPropagation()}
                  >
                    {group.models.map(model => (
                      <button
                        key={model.id}
                        type="button"
                        onClick={() => selectModel(group, model.id)}
                        className={cn(
                          'shrink-0 snap-start rounded-lg border px-2.5 py-1.5 text-left transition-colors',
                          isActive && groupChoice === model.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/30',
                        )}
                      >
                        <p className="whitespace-nowrap text-xs font-semibold">{model.name}</p>
                        <p className="whitespace-nowrap text-[10px] text-muted-foreground">
                          {model.tier} · {model.credits_per_scene}cr/scene
                        </p>
                      </button>
                    ))}
                  </div>
                )}

                {/* Style carousel — picking a style swaps the card preview above */}
                {isActive && styleOptions.length > 1 && (
                  <div
                    className="pt-1.5"
                    onClick={event => event.stopPropagation()}
                    onKeyDown={event => event.stopPropagation()}
                  >
                    <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Style
                    </p>
                    <div className="flex snap-x gap-2 overflow-x-auto">
                      {styleOptions.map(style => (
                        <button
                          key={style.id}
                          type="button"
                          onClick={() => selectStyle(style.id)}
                          className={cn(
                            'shrink-0 snap-start overflow-hidden rounded-md border transition-colors',
                            effectiveStyle === style.id
                              ? 'border-primary'
                              : 'border-border hover:border-primary/30',
                          )}
                        >
                          {style.preview_url && (
                            <img
                              src={style.preview_url}
                              className="h-12 w-20 object-cover"
                              alt={style.name}
                            />
                          )}
                          <p className="px-1.5 py-1 text-center text-[10px] font-medium">
                            {style.name}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
